import { ChevronV2 } from 'assets/icons/ChevronV2'
import { FieldWrapper, Input, InputWrapper, InvalidText, Label } from 'components/Form'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import {
  MultiSelectInput,
  Remove,
  Pill,
  MultiSelectFieldWrapper,
  SelectOption,
  SelectOptionsContainer,
} from './Select.styles'
import { Option } from './Select'
import { useQuery } from '@tanstack/react-query'

export const AsyncMultiselect = <T,>({
  loadOptions,
  decoder,
  values,
  onChange,
  label,
  placeholder,
  width,
  error,
  queryKey,
}: {
  loadOptions: (search: string) => Promise<T[]>
  decoder: (item: T) => Option
  values?: Option[] | null
  onChange: (value: Option[] | null) => void
  label?: string
  placeholder?: string
  width?: string
  error?: string
  queryKey: string
}) => {
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { data } = useQuery({
    queryKey: [queryKey, inputValue, values],
    queryFn: () => loadOptions(inputValue || undefined),
  })
  const options = data?.map(decoder) || []
  const inputRef = useRef<HTMLInputElement>(null)

  const calculatePosition = () => {
    const rect = inputRef.current?.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const windowWidth = window.innerWidth
    const containerHeight = 200
    const containerWidth = 200
    if (!rect)
      return {
        top: 0,
        left: 0,
      }
    const position = {
      top: rect.top + 50,
      left: rect.left,
    }

    if (position.top + containerHeight > windowHeight) {
      return {
        bottom: windowHeight - rect.top + 10,
        left: rect.left,
      }
    }
    if (position.left + containerWidth > windowWidth) {
      position.left = rect.left - containerWidth
    }
    return {
      top: position.top,
      left: position.left,
    }
  }

  useEffect(() => {
    inputRef.current?.addEventListener('focus', () => {
      setOpen(true)
    })
    inputRef.current?.addEventListener('blur', () => {
      setTimeout(() => {
        setOpen(false)
      }, 100)
    })
  }, [])

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setInputValue('')
        inputRef?.current?.blur()
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  const handleRemove = (value: Option) => {
    onChange(values?.filter((v) => v?.value !== value?.value) || null)
  }

  return (
    <InputWrapper width={width}>
      {label && <Label>{label}</Label>}

      <FieldWrapper>
        <MultiSelectFieldWrapper onClick={() => inputRef.current?.focus()} invalid={!!error}>
          {values?.map((v) => (
            <Pill key={v?.value}>
              <span>{v?.label}</span>
              <Remove
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove(v)
                }}
              >
                ×
              </Remove>
            </Pill>
          ))}

          <MultiSelectInput
            ref={inputRef}
            value={inputValue}
            placeholder={values?.length ? '' : placeholder}
            onChange={(e) => setInputValue(e.target.value)}
            onFocus={() => setOpen(true)}
          />

          <ChevronIconContainer>
            <ChevronV2 open={open} />
          </ChevronIconContainer>
        </MultiSelectFieldWrapper>
      </FieldWrapper>
      {error && <InvalidText>{error}</InvalidText>}

      {open &&
        createPortal(
          <SelectOptionsContainer
            style={{
              ...calculatePosition(),
            }}
            ref={dropdownRef}
          >
            {options.map((option) => (
              <SelectOption
                key={option.value}
                active={values?.some((value) => value.value === option.value)}
                onMouseDown={() => {
                  if (values?.some((v) => v?.value === option.value)) {
                    handleRemove(option)
                    return
                  }
                  onChange([...(values || []), option])
                  setInputValue('')
                  setOpen(false)
                  inputRef?.current?.blur()
                }}
              >
                {option.label}
              </SelectOption>
            ))}
          </SelectOptionsContainer>,
          document.body,
        )}
    </InputWrapper>
  )
}

const ChevronIconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  height: 1.5rem;
  color: ${(props) => props.theme.textColor};
`
